"use client";

import { ClockIcon } from "@heroicons/react/24/outline";

import Icon from "@/components/atoms/Icon";
import type { OpeningHour } from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import {
  dayNumberToString,
  getNextOpeningHour,
  isOpenNow,
  nextClosingHour,
} from "@/helpers/openingHours";
import { AllowedLanguages } from "@/types/generic";

interface IsOpenMessageProps {
  className?: string;
  iconClassName?: string;
  iconWrapperClassName?: string;
  locale: AllowedLanguages;
  opening_hours: OpeningHour[];
}

const getDayName = (day: number, locale: AllowedLanguages) => {
  const today = new Date().getDay();
  if (day === today) return ""; // Can also return 'Today'

  return dayNumberToString(day, locale);
};

const i18n = {
  nl: {
    open: "Geopend",
    closed: "Gesloten",
    closes_at: "Sluit om",
    opens: "Opent",
    at: "om",
    available_by_phone: "Telefonisch bereikbaar tot 17:30",
  },
  fr: {
    open: "Ouvert",
    closed: "Fermé",
    closes_at: "Ferme à",
    opens: "Ouvre",
    at: "à",
    available_by_phone: "Disponible par téléphone jusqu'à 17:30",
  },
  en: {
    open: "Open",
    closed: "Closed",
    closes_at: "Closes at",
    opens: "Opens",
    at: "at",
    available_by_phone: "Available by phone until 17:30",
  },
};

export const IsOpenMessage = ({
  className,
  iconClassName,
  iconWrapperClassName,
  opening_hours,
  locale,
}: IsOpenMessageProps) => {
  const isOpen = isOpenNow(opening_hours);
  const nextOpeningDay = getNextOpeningHour(opening_hours)?.day;

  return (
    <div className={cn("flex items-start gap-4 text-gray-500", className)}>
      <span
        className={cn(
          "inline-flex h-12 w-12 items-center justify-center border",
          iconWrapperClassName,
        )}
      >
        <ClockIcon className={cn("h-5 w-5", iconClassName)} />
      </span>
      <div className="flex flex-col gap-1">
        {isOpen ? (
          <>
            <div className="flex items-center gap-1">
              <Icon name="dot" className="mr-1 h-2 w-2 text-green" />
              <span className="font-medium text-positive">
                {i18n[locale].open}
              </span>
              -
              <span className="text-positive">
                {i18n[locale].closes_at}{" "}
                {nextClosingHour(opening_hours)?.closing_hour}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="dot" className="mr-1 h-2 w-2 text-green" />
              <span className="font-medium text-positive">
                {i18n[locale].available_by_phone}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <Icon name="dot" className="mr-1 h-2 w-2 text-black" />
              <span className="font-medium text-black">
                {i18n[locale].closed}
              </span>
              -
              <span className="text-black">
                {i18n[locale].opens}{" "}
                <strong>
                  {nextOpeningDay ? getDayName(nextOpeningDay, locale) : null}
                </strong>{" "}
                {i18n[locale].at}{" "}
                <strong>
                  {getNextOpeningHour(opening_hours)?.opening_hour}
                </strong>
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="dot" className="mr-1 h-2 w-2 text-black" />
              <span className="font-medium text-black">
                {i18n[locale].available_by_phone}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IsOpenMessage;
